import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { 
  GraduationCap, 
  Mail,
  Lock,
  CheckCircle,
  Loader2,
  ArrowLeft,
  KeyRound
} from "lucide-react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../components/ui/input-otp";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  // Steps: 1 = Enter Email, 2 = Enter OTP + New Password, 3 = Complete
  const [currentStep, setCurrentStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [processing, setProcessing] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleRequestReset = async () => {
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setProcessing(true);
    try {
      await axios.post(`${API}/auth/password-reset/request`, { email });
      toast.success("If an account exists with this email, a reset code has been sent.");
      setCurrentStep(2);
      setResendCooldown(60);
    } catch (error) {
      toast.error("Failed to send reset code");
    } finally {
      setProcessing(false);
    }
  };

  const handleResetPassword = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter the complete 6-digit code");
      return;
    }
    
    if (!newPassword || newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setProcessing(true);
    try {
      await axios.post(`${API}/auth/password-reset/verify`, {
        email,
        otp,
        new_password: newPassword
      });
      toast.success("Password reset successfully!");
      setCurrentStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setProcessing(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/auth/password-reset/request`, { email });
      toast.success("New reset code sent");
      setResendCooldown(60);
    } catch (error) {
      toast.error("Failed to resend code");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 to-stone-50 py-12 px-4 flex items-center justify-center">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <GraduationCap className="h-8 w-8 text-violet-700" />
            <span className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
              LearnHub
            </span>
          </Link>
          <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
            Reset Your Password
          </h1>
        </div>

        <Card className="shadow-lg">
          <CardContent className="p-8">
            {/* Step 1: Enter Email */}
            {currentStep === 1 && (
              <div className="space-y-6" data-testid="reset-step-1">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <KeyRound className="h-8 w-8 text-violet-700" />
                  </div>
                  <p className="text-stone-600 text-sm">
                    Enter your email address and we'll send you a code to reset your password.
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="reset-email-input"
                  />
                </div>
                
                <Button 
                  onClick={handleRequestReset}
                  disabled={processing}
                  className="w-full bg-violet-700 hover:bg-violet-800"
                  data-testid="request-reset-btn"
                >
                  {processing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Send Reset Code
                    </>
                  )}
                </Button>
                
                <div className="text-center">
                  <Link to="/login" className="text-sm text-violet-700 hover:underline inline-flex items-center gap-1">
                    <ArrowLeft className="h-4 w-4" />
                    Back to Sign In
                  </Link>
                </div>
              </div>
            )}

            {/* Step 2: Enter OTP + New Password */}
            {currentStep === 2 && (
              <div className="space-y-6" data-testid="reset-step-2">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="h-8 w-8 text-violet-700" />
                  </div>
                  <p className="text-stone-600 text-sm">
                    Enter the code sent to<br />
                    <span className="font-medium text-stone-900">{email}</span>
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label>Verification Code</Label>
                    <div className="flex justify-center mt-2">
                      <InputOTP
                        maxLength={6}
                        value={otp}
                        onChange={setOtp}
                        data-testid="reset-otp-input"
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      placeholder="Minimum 8 characters"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      data-testid="new-password-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Re-enter your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      data-testid="confirm-new-password-input"
                    />
                  </div>
                </div>
                
                <Button 
                  onClick={handleResetPassword}
                  disabled={processing || otp.length !== 6}
                  className="w-full bg-violet-700 hover:bg-violet-800"
                  data-testid="reset-password-btn"
                >
                  {processing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Reset Password
                    </>
                  )}
                </Button>
                
                <div className="text-center space-y-2">
                  <p className="text-sm text-stone-600">
                    Didn't receive the code?{" "}
                    <button
                      onClick={handleResendOTP}
                      disabled={resendCooldown > 0 || processing}
                      className={`${
                        resendCooldown > 0 
                          ? 'text-stone-400 cursor-not-allowed' 
                          : 'text-violet-700 hover:underline'
                      }`}
                    >
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                    </button>
                  </p>
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="text-sm text-stone-500 hover:text-stone-700"
                  >
                    Use different email
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Complete */}
            {currentStep === 3 && (
              <div className="text-center space-y-6" data-testid="reset-step-3">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                
                <div>
                  <h2 className="text-xl font-bold text-stone-900">Password Reset!</h2>
                  <p className="text-stone-600 mt-2">
                    Your password has been reset successfully. You can now sign in with your new password.
                  </p>
                </div>
                
                <Button 
                  onClick={() => navigate('/login')}
                  className="w-full bg-violet-700 hover:bg-violet-800"
                >
                  Sign In
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
